/**
 * Firebase Context
 * 
 * This context provides Firebase services to the entire app.
 * It wraps the Firebase initialization and configuration.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  app, 
  db, 
  auth, 
  storage, 
  functions, 
  analytics, 
  appCheck, 
  isDevelopment,
  checkFirebaseConnectivity,
  forceNetworkReset
} from '../firebase.js';

const FirebaseContext = createContext(null);

// Custom hook to use the Firebase context
export const useFirebase = () => useContext(FirebaseContext);

// Provider component
export const FirebaseProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: navigator.onLine,
    isChecking: false,
    lastChecked: null,
    error: null
  });
  
  // Set up online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkConnection();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setConnectionStatus(prev => ({
        ...prev,
        isConnected: false,
        lastChecked: new Date()
      }));
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check connection on mount
    checkConnection();
    
    // Mark Firebase as initialized
    setInitialized(true);
    
    return () => {
      // Remove listeners
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Function to check Firebase connectivity
  const checkConnection = async () => {
    if (!navigator.onLine) {
      setConnectionStatus({
        isConnected: false,
        isChecking: false,
        lastChecked: new Date(),
        error: { reason: 'DEVICE_OFFLINE' }
      });
      return;
    }
    
    setConnectionStatus(prev => ({ ...prev, isChecking: true }));
    
    try {
      const result = await checkFirebaseConnectivity();
      setConnectionStatus({
        isConnected: result.isConnected,
        isChecking: false,
        lastChecked: new Date(),
        error: result.isConnected ? null : { reason: result.reason, error: result.error }
      });
    } catch (error) {
      setConnectionStatus({
        isConnected: false,
        isChecking: false,
        lastChecked: new Date(),
        error: { reason: 'CHECK_FAILED', error }
      });
    }
  };
  
  // Function to reset Firebase connection
  const resetConnection = async () => {
    setConnectionStatus(prev => ({ ...prev, isChecking: true }));
    
    try {
      const result = await forceNetworkReset();
      if (result.success) {
        await checkConnection();
      } else {
        setConnectionStatus({
          isConnected: false,
          isChecking: false,
          lastChecked: new Date(),
          error: { reason: 'RESET_FAILED', error: result.error }
        });
      }
    } catch (error) {
      setConnectionStatus({
        isConnected: false,
        isChecking: false,
        lastChecked: new Date(),
        error: { reason: 'RESET_FAILED', error }
      });
    }
  };
  
  // Provide the Firebase services and state
  const value = {
    app,
    db,
    auth,
    storage,
    functions,
    analytics,
    appCheck,
    isDevelopment,
    initialized,
    isOnline,
    connection: connectionStatus,
    checkConnection,
    resetConnection
  };
  
  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext; 